import type {
	CreateWatchParams,
	UpdateWatchParams,
	Watch,
	WatchDto,
	WatchStatus,
} from "@sitescene/types";

import { apiClient, type ApiResponse, endpoints } from "@/api";

export interface WatchFilterParams {
	after?: string;
	/** Exact alert-count match (0 = none, 1 = one). */
	alertCount?: number;
	includeArchived?: boolean;
	limit?: number;
	/** Location ids to match; sent as the `location[]` array query param. */
	location?: string[];
	minAlerts?: number;
	/** Case-insensitive match on title or description. */
	search?: string;
	sortDirection?: number;
	sortField?: string;
	status?: WatchStatus;
}

export const watchesApi = {
	createWatch: async (params: CreateWatchParams): Promise<Watch> => {
		const response = await apiClient.post<Watch>(
			endpoints.watches.create,
			params,
		);
		return response.data;
	},

	getWatch: async (watchId: string): Promise<WatchDto> => {
		const response = await apiClient.get<WatchDto>(
			endpoints.watches.getById(watchId),
		);
		return response.data;
	},

	getWatchesPaginated: async (
		params: WatchFilterParams = {},
	): Promise<ApiResponse<WatchDto[]>> => {
		const {
			after,
			alertCount,
			includeArchived,
			limit = 10,
			location,
			minAlerts,
			search,
			sortDirection,
			sortField,
			status,
		} = params;

		const searchParams = new URLSearchParams();

		if (limit !== undefined) {
			searchParams.append("limit", limit.toString());
		}
		if (after) {
			searchParams.append("after", after);
		}
		// `location[]` array convention — the API middleware splits on commas
		// and strips the suffix.
		if (location && location.length > 0) {
			searchParams.append("location[]", location.join(","));
		}
		if (search) {
			searchParams.append("search", search);
		}
		if (status) {
			searchParams.append("status", status);
		}
		if (alertCount !== undefined) {
			searchParams.append("alertCount", alertCount.toString());
		}
		if (minAlerts !== undefined) {
			searchParams.append("minAlerts", minAlerts.toString());
		}
		if (includeArchived !== undefined) {
			searchParams.append("includeArchived", String(includeArchived));
		}
		if (sortField) {
			searchParams.append("sortField", sortField);
		}
		if (sortDirection !== undefined) {
			searchParams.append("sortDirection", sortDirection.toString());
		}

		const query = searchParams.toString();
		const endpoint = query
			? `${endpoints.watches.get}?${query}`
			: endpoints.watches.get;

		return apiClient.get<WatchDto[]>(endpoint);
	},

	updateWatch: async (
		watchId: string,
		params: UpdateWatchParams,
	): Promise<Watch> => {
		const response = await apiClient.patch<Watch>(
			endpoints.watches.update(watchId),
			params,
		);
		return response.data;
	},
};
